import { useEffect, useState, useCallback } from "react";
import SelectProfesiones from "./SelectProfesiones";
import EligeCantidadPago from "./EligeCantidadPago";
import { ButtonClose } from "../../ui";
import {
  FormularioPaso2Props,
  PaymentOption,
  SelectProfesionesProps,
} from "@/types";
import { safeFetch } from "@/api/safeFetch";
import { PopupCodigoProfesion } from "@/components/popup";

const FormularioPaso2: React.FC<FormularioPaso2Props> = ({
  handleProfesionSelect,
  selectedProfesiones,
  onPaymentChange,
  errors,
  setErrors,
}) => {

  // --------------------------
  // STATES
  // --------------------------
  const [opciones, setOpciones] = useState<SelectProfesionesProps[]>([]);
  const [loadingProfesiones, setLoadingProfesiones] = useState(true);

  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<PaymentOption>("");

  const [paymentAmount, setPaymentAmount] = useState(0);
  const [, setErrorFetch] = useState<string | null>(null);

  const profesiones = selectedProfesiones ?? [];

  // POPUP
  const [showModal, setShowModal] = useState(false);
  const [profesionPendiente, setProfesionPendiente] = useState<string | null>(null);
  const [codigoInput, setCodigoInput] = useState("");
  const [errorCodigo, setErrorCodigo] = useState("");
  const [loadingCodigo, setLoadingCodigo] = useState(false);

  // --------------------------
  // FETCH
  // --------------------------
  useEffect(() => {
    const fetchProfesiones = async () => {
      setLoadingProfesiones(true);
      try {
        const response = await safeFetch(
          `/api/profesiones/sacarProfesion/registrarse`
        );

        if (!response.ok) throw new Error();

        const data: SelectProfesionesProps[] = await response.json();
        setOpciones(data);
      } catch (error) {
        setErrorFetch("Error al cargar profesiones");
      } finally {
        setLoadingProfesiones(false);
      }
    };

    fetchProfesiones();
  }, []);

  // --------------------------
  // SELECT PROFESIONES (FIX IMPORTANTE)
  // --------------------------
  const handleProfessionSelect = (selected: string[]) => {
    const nueva = selected.find((p) => !profesiones.includes(p));
    if (!nueva) return;

    const profesionObj = opciones.find(
      (p) => p.nombre_profesion === nueva
    );

    // 🔐 requiere código → abrir popup
    if (profesionObj?.requiere_codigo) {
      setProfesionPendiente(nueva);
      setCodigoInput("");
      setErrorCodigo("");
      setShowModal(true);

      // 🔥 CLAVE: NO tocar select todavía
      return;
    }

    // ✔ selección normal
    handleProfesionSelect?.([nueva]);

    setErrors?.((prev) => ({
      ...prev,
      selectedProfession: false,
    }));
  };

  // --------------------------
  // CANCELAR POPUP (FIX CLAVE)
  // --------------------------
  const handleCancelarCodigo = () => {
    setProfesionPendiente(null);
    setCodigoInput("");
    setErrorCodigo("");
    setShowModal(false);

    // 🔥 LIMPIAR SELECT SOLO AQUÍ
    handleProfesionSelect?.([]);
  };

  // --------------------------
  // ACEPTAR CÓDIGO
  // --------------------------
  const handleGuardarCodigo = async () => {
    if (!profesionPendiente || !codigoInput.trim()) return;

    setLoadingCodigo(true);
    setErrorCodigo("");

    try {
      const res = await safeFetch("/api/profesiones/validar-codigo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profesion: profesionPendiente,
          codigo: codigoInput,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.valid) {
        setErrorCodigo(data.message || "Código incorrecto");
        return;
      }

      // ✔ aceptar solo 1
      handleProfesionSelect?.([profesionPendiente]);

      setShowModal(false);
      setProfesionPendiente(null);
      setCodigoInput("");

    } catch {
      setErrorCodigo("Error validando código");
    } finally {
      setLoadingCodigo(false);
    }
  };

  // --------------------------
  // ELIMINAR PROFESIÓN
  // --------------------------
  const handleRemoveProfesion = useCallback(() => {
    handleProfesionSelect?.([]);
  }, [handleProfesionSelect]);

  // --------------------------
  // RENDER (NO TOCO TU DISEÑO)
  // --------------------------
  return (
    <>
      <div className="grid grid-cols-12 gap-4 xs:text-sm md:text-md py-4 items-center dark:text-zinc-800">

        {/* SELECT (NO TOCADO) */}
        <div className="lg:col-span-3 md:col-span-6 sm:col-span-12 xs:col-span-12 p-4 rounded-lg border">
          {loadingProfesiones ? (
            <p className="dark:text-zinc-600">Cargando profesiones...</p>
          ) : (
            <SelectProfesiones
              nombresProfesiones={opciones
                .sort((a, b) =>
                  (a.nombre_profesion ?? "").localeCompare(
                    b.nombre_profesion ?? ""
                  )
                )
                .map((opcion) => opcion.nombre_profesion ?? "")}
              selectedProfesiones={selectedProfesiones}
              onSelectionChange={handleProfessionSelect}
              errors={errors}
              name="selectedProfession"
              className={`w-full truncate overflow-hidden px-3 py-2 rounded-md border
                dark:text-zinc-700 
                cursor-text 
                shadow-[inset_-2px_-2px_1px_rgb(99,105,112)] 
                focus:shadow-[inset_2px_3px_2px_rgb(24,25,27)]
                outline-none
                transition-all duration-200 ${errors?.selectedProfession ? 'bg-red-100' : ''}`}
            />
          )}
        </div>

        {/* LISTA (NO TOCADO) */}
        <div className="lg:col-span-3 md:col-span-6 sm:col-span-12 xs:col-span-12 p-4 rounded-lg min-h-28">
          <h3 className="mb-4 font-semibold">Profesiones seleccionadas:</h3>

          <div className="flex flex-col gap-2">
            {profesiones.length > 0 ? (
              profesiones.map((profesion, index) => (
                <div
                  key={index}
                  className="flex w-full items-center justify-between font-extrabold text-zinc-900 bg-zinc-400 px-1 py-1 rounded-md border border-zinc-600 shadow-md shadow-blue-700 hover:shadow-red-700"
                >
                  <span className="truncate overflow-hidden whitespace-nowrap w-full">
                    {profesion}
                  </span>
                  <ButtonClose
                    className="flex-shrink-0"
                    onClick={handleRemoveProfesion}
                  />
                </div>
              ))
            ) : (
              <p>No hay profesiones seleccionadas.</p>
            )}
          </div>
        </div>

        {/* PAGO (NO TOCADO) */}
        <div className={`lg:col-span-6 md:col-span-6 sm:col-span-12 xs:col-span-12 p-4 rounded-lg border ${errors?.payment ? 'bg-red-100 border-red-600' : ''}`}>
          <EligeCantidadPago
            selectedPaymentOption={selectedPaymentOption}
            setSelectedPaymentOption={setSelectedPaymentOption}
            setPaymentAmount={setPaymentAmount}
            paymentAmount={paymentAmount}
            errors={errors || {}}
            onPaymentChange={(opt, amt) => {
              setSelectedPaymentOption(opt);
              setPaymentAmount(amt);
              onPaymentChange?.(opt, amt);
            }}
          />
        </div>

      </div>

      {/* POPUP */}
      <PopupCodigoProfesion
        show={showModal}
        profesion={profesionPendiente}
        codigo={codigoInput}
        setCodigo={setCodigoInput}
        onCancel={handleCancelarCodigo}
        onAccept={handleGuardarCodigo}
        errorCodigo={errorCodigo}
        loading={loadingCodigo}
      />
    </>
  );
};

export default FormularioPaso2;